import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

enum MessageType {
  RESEARCH_REPORT = "research_report",
  MESSAGE = "message",
  TASK_NOTIFICATION = "task_notification",
  TASK_NOTIFICATION_FINAL = "task_notification_final",
  REDIRECT = "redirect",
  MARKDOWN = "markdown",
  SYSTEM_MESSAGE = "system_message",
  RETRYABLE_ERROR = "retryable_error",
}
export interface Message {
  message_type: MessageType;
  raw_text: string;
  author_type: "human" | "assistant" | "system";
  timestamp?: Date;
  message_id?: string;
}

function buildMessage(data: any): Message {
  return Object.assign({}, data, {
    timestamp: data.timestamp ? new Date(data.timestamp) : undefined,
  });
}

export type ConnectionStatus =
  | "connecting"
  | "connected"
  | "disconnected"
  | "error";

interface UseWebSocketChatProps {
  endpoint: string; // e.g., "ws/chat/" or "ws/onboarding/"
  queryParams?: Record<string, string>;
}

interface UseWebSocketChatReturn {
  messages: Message[];
  inputMessage: string;
  setInputMessage: (message: string) => void;
  status: ConnectionStatus;
  error: string | null;
  isTyping: boolean;
  handleSend: () => void;
  handleKeyPress: (e: React.KeyboardEvent) => void;
  connectWebSocket: () => void;
  currentTaskNotification: Message | null;
}

export const useWebSocketChat = ({
  endpoint,
  queryParams,
}: UseWebSocketChatProps): UseWebSocketChatReturn => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState("");
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const [error, setError] = useState<string | null>(null);
  const [isTyping, setIsTyping] = useState(false);
  const [ws, setWs] = useState<WebSocket | null>(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [currentTaskNotification, setCurrentTaskNotification] = useState<Message | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const navigate = useNavigate();

  const isMountedRef = useRef(true);
  const reconnectAttemptsRef = useRef(0);
  const lastConnectTimeRef = useRef(0);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const connectWebSocket = useCallback(() => {
    if (!isMountedRef.current) return;

    const now = Date.now();
    const timeSinceLastConnect = now - lastConnectTimeRef.current;

    if (timeSinceLastConnect < 1000) {
      reconnectAttemptsRef.current++;

      if (reconnectAttemptsRef.current > 3) {
        const backoffTime = Math.min(30000, Math.pow(2, reconnectAttemptsRef.current - 3) * 1000);

        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }

        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            connectWebSocket();
          }
        }, backoffTime);

        return;
      }
    } else {
      reconnectAttemptsRef.current = 0;
    }

    lastConnectTimeRef.current = now;

    if (isConnecting || ws?.readyState === WebSocket.CONNECTING) {
      return;
    }

    setIsConnecting(true);

    if (ws) {
      ws.close();
      setWs(null);
    }

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const wsUrl = `${protocol}//${window.location.host}${endpoint}${
      queryParams ? `?${new URLSearchParams(queryParams)}` : ''
    }`;

    const newWs = new WebSocket(wsUrl);

    newWs.onopen = () => {
      if (!isMountedRef.current) return;
      setIsConnecting(false);
      setStatus("connected");
      reconnectAttemptsRef.current = 0;
    };

    newWs.onclose = (event) => {
      if (!isMountedRef.current) return;

      setIsConnecting(false);
      setWs(null);
      setStatus("disconnected");

      if (event.code === 1000) {
        // Normal closure - don't reconnect
      } else if (event.code === 1008) {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            connectWebSocket();
          }
        }, 5000);
      } else if (event.code === 1013) {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            connectWebSocket();
          }
        }, 15000);
      } else {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
        }
        const backoffTime = Math.min(10000, 1000 * Math.pow(1.5, Math.min(5, reconnectAttemptsRef.current)));
        reconnectTimeoutRef.current = setTimeout(() => {
          if (isMountedRef.current) {
            connectWebSocket();
          }
        }, backoffTime);
      }
    };

    newWs.onerror = (event) => {
      setStatus("error");
      setError("WebSocket connection error");
    };

    const handleRedirectMessage = (data: any) => {
      navigate(data.url);
    };

    const handleTaskNotification = (data: any) => {
      const message = buildMessage(data);
      setCurrentTaskNotification(message);
    };

    const handleHumanMessage = (data: any) => {
      setMessages((prevMessages) => {
        if (data.message_id) {
          const messageIndex = prevMessages.findIndex(
            (msg) => msg.message_id === data.message_id
          );

          if (messageIndex !== -1) {
            const updatedMessages = [...prevMessages];
            updatedMessages[messageIndex] = {
              ...updatedMessages[messageIndex],
              timestamp: new Date(data.timestamp),
            };
            return updatedMessages;
          }
        }

        const messageIndex = prevMessages.findIndex(
          (msg) =>
            msg.author_type === "human" &&
            msg.raw_text.trim() === data.raw_text.trim() &&
            !msg.timestamp
        );

        if (messageIndex !== -1) {
          const updatedMessages = [...prevMessages];
          updatedMessages[messageIndex] = {
            ...updatedMessages[messageIndex],
            timestamp: new Date(data.timestamp),
            message_id: data.message_id,
          };
          return updatedMessages;
        }

        return [...prevMessages, buildMessage(data)]
      });
    };

    const handleAssistantOrSystemMessage = (data: any) => {
      const message: Message = buildMessage(data);
      setMessages((prev) => [...prev, message]);
      setIsTyping(false);
    };

    const handleRegularMessage = (data: any) => {
      if (data.author_type === "human") {
        handleHumanMessage(data);
      } else {
        handleAssistantOrSystemMessage(data);
      }
    };

    newWs.onmessage = (event) => {
      if (!isMountedRef.current) return;

      try {
        const data = JSON.parse(event.data);

        switch (data.message_type) {
          case MessageType.REDIRECT:
            handleRedirectMessage(data);
            break;

          case MessageType.TASK_NOTIFICATION:
          case MessageType.TASK_NOTIFICATION_FINAL:
            handleTaskNotification(data);
            break;

          case MessageType.MESSAGE:
          case MessageType.MARKDOWN:
          case MessageType.RESEARCH_REPORT:
          case MessageType.SYSTEM_MESSAGE:
            handleRegularMessage(data);
            break;

          default:
            break;
        }
      } catch (err) {
        setError("Error processing message from server");
      }
    };

    setWs(newWs);
  }, [endpoint, queryParams, navigate]);

  useEffect(() => {
    connectWebSocket();

    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (ws) {
        ws.close(1000, "Component unmounting");
      }
    };
  }, [connectWebSocket]);

  const handleSend = () => {
    if (!inputMessage.trim() || status !== "connected") return;

    const message: Message = buildMessage({
      author_type: "human",
      message_type: MessageType.MESSAGE,
      raw_text: inputMessage,
    });

    try {
      ws?.send(JSON.stringify(message));
      setMessages((prev) => [...prev, message]);
      setInputMessage("");
      setIsTyping(true);
    } catch (err) {
      console.error("Error sending message:", err);
      setError("Failed to send message");
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return {
    messages,
    inputMessage,
    setInputMessage,
    status,
    error,
    isTyping,
    handleSend,
    handleKeyPress,
    connectWebSocket,
    currentTaskNotification,
  };
};
